import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box, Typography, Grid, Button, Chip, Divider,
  IconButton, Skeleton, Paper, Breadcrumbs, Link, Alert, Container, LinearProgress
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import InventoryIcon from '@mui/icons-material/Inventory';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import SEO from '../components/SEO';
import SocialShare from '../components/SocialShare';
import { RelatedProducts } from '../components/Recommendations';
import useBehaviorTracking from '../hooks/useBehaviorTracking';
import { trackTikTokViewContent, trackTikTokAddToCart } from '../components/TikTokPixel';
import StarRating from '../components/StarRating';
import ProductReviews from '../components/ProductReviews';
import ReviewForm from '../components/ReviewForm';
import RateReviewIcon from '@mui/icons-material/RateReview';

// 修復圖片 URL（處理雙斜線問題）
const fixImageUrl = (url) => {
  if (!url) return 'https://via.placeholder.com/600x400?text=商品圖片';
  // 修復雙斜線問題：https://www.ezp.com.tw//images -> https://www.ezp.com.tw/images
  return url.replace(/([^:]\/)\/+/g, '$1');
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState({}); // 選中的規格選項
  const [selectedImageIndex, setSelectedImageIndex] = useState(0); // 當前選中的圖片索引
  const [showReviewForm, setShowReviewForm] = useState(false); // 顯示評論表單
  const [reviewSummary, setReviewSummary] = useState(null); // 評論摘要
  // 移除假性倒計時器 - Google Merchant Center 不允許假性緊迫感行銷
  const { trackView, trackCartAdd } = useBehaviorTracking();

  // 解析商品圖片列表
  const parseImages = (product) => {
    const images = [];
    // 1. 主圖
    if (product.image_url) {
      images.push(fixImageUrl(product.image_url));
    }
    // 2. images 欄位（JSON 陣列）
    if (product.images) {
      try {
        const additionalImages = typeof product.images === 'string'
          ? JSON.parse(product.images)
          : product.images;
        if (Array.isArray(additionalImages)) {
          additionalImages.forEach(img => {
            const fixedImg = fixImageUrl(img);
            if (fixedImg && !images.includes(fixedImg)) {
              images.push(fixedImg);
            }
          });
        }
      } catch (e) {
        console.log('解析 images 失敗:', e);
      }
    }
    // 3. 從描述中提取圖片（排除已有的）
    if (product.description) {
      const decoded = product.description
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
      const imgRegex = /src\s*=\s*['"]([^'"]+)['"]/g;
      let match;
      while ((match = imgRegex.exec(decoded)) !== null) {
        let imgUrl = match[1];
        // 轉換相對路徑為 YMShop 絕對路徑
        if (imgUrl.startsWith('/image/')) {
          imgUrl = `https://www.ymshop.com.tw${imgUrl}`;
        }
        if (!images.includes(imgUrl)) {
          images.push(imgUrl);
        }
      }
    }
    return images.length > 0 ? images : ['https://via.placeholder.com/600x400?text=商品圖片'];
  };

  // 解析商品規格 JSON
  const parseSpecs = (specsJson) => {
    if (!specsJson) return [];
    try {
      return typeof specsJson === 'string' ? JSON.parse(specsJson) : specsJson;
    } catch {
      return [];
    }
  };

  // 計算規格加價
  const getOptionsPrice = () => {
    if (!product?.specs) return 0;
    const specs = parseSpecs(product.specs);
    let extraPrice = 0;
    specs.forEach(spec => {
      const selected = selectedOptions[spec.option_name];
      if (selected) {
        const option = spec.values.find(v => v.name === selected);
        if (option && option.prefix === '+') {
          extraPrice += parseFloat(option.price) || 0;
        }
      }
    });
    return extraPrice;
  };

  // 頁面載入時滾動到頂部
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        // 並行抓取商品資料 + 評論摘要，大幅減少載入時間
        const [productResult, reviewResult] = await Promise.allSettled([
          api.get(`/products/${id}`),
          api.get(`/reviews/product/${id}`)
        ]);

        // 處理商品資料
        if (productResult.status === 'rejected') {
          throw productResult.reason;
        }
        const productData = productResult.value.data;
        setProduct(productData);

        // 處理評論摘要（失敗不影響頁面）
        if (reviewResult.status === 'fulfilled' && reviewResult.value.data.success) {
          setReviewSummary(reviewResult.value.data.data.summary);
        }

        // 保存瀏覽紀錄到 localStorage
        if (productData) {
          const viewedHistory = JSON.parse(localStorage.getItem('viewedHistory') || '[]');
          const filtered = viewedHistory.filter(item => item.id !== productData.id);
          const newHistory = [{
            id: productData.id,
            name: productData.name,
            image: productData.image_url,
            price: productData.sale_price,
            viewedAt: new Date().toISOString()
          }, ...filtered].slice(0, 20);
          localStorage.setItem('viewedHistory', JSON.stringify(newHistory));
        }

        // 追蹤事件延後執行，不阻擋渲染（使用數字 ID，避免 SEO URL 字串導致 DB 錯誤）
        setTimeout(() => {
          trackView(productData.id);

          // GTM/GA4 商品瀏覽事件
          if (window.dataLayer && productData) {
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push({
              event: 'view_item',
              ecommerce: {
                currency: 'TWD',
                value: productData.sale_price,
                items: [{
                  item_id: productData.sku,
                  item_name: productData.name,
                  item_category: productData.category_name,
                  price: productData.sale_price,
                  quantity: 1
                }]
              }
            });
          }

          // Facebook Pixel 商品瀏覽事件
          if (window.fbq && productData) {
            window.fbq('track', 'ViewContent', {
              content_type: 'product',
              content_ids: [productData.sku],
              content_name: productData.name,
              content_category: productData.category_name,
              value: productData.sale_price,
              currency: 'TWD'
            });
          }

          // TikTok Pixel 商品瀏覽事件
          if (productData) {
            trackTikTokViewContent(productData);
          }
        }, 0);
      } catch (error) {
        console.error('取得商品失敗:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const handleAddToCart = async () => {
    // 檢查是否有規格需要選擇
    const specs = parseSpecs(product.specs);
    if (specs.length > 0) {
      // 檢查是否有必選的規格未選擇
      const requiredSpecs = specs.filter(spec => spec.required);
      const missingRequired = requiredSpecs.filter(spec => !selectedOptions[spec.option_name]);

      if (missingRequired.length > 0) {
        toast.error(`請選擇：${missingRequired.map(s => s.option_name).join('、')}`);
        return false;
      }
    }

    // 計算最終價格（含規格加價）
    const optionsPrice = getOptionsPrice();
    const finalPrice = parseFloat(product.sale_price) + optionsPrice;
    const totalValue = finalPrice * quantity;

    // 準備規格信息
    const specInfo = specs.length > 0 ? {
      options: selectedOptions,
      optionsPrice: optionsPrice,
      finalPrice: finalPrice
    } : null;

    const success = await addToCart(product.id, quantity, specInfo);
    if (success) {
      // 記錄加入購物車行為
      trackCartAdd(product.id);

      // GTM 加入購物車事件
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'add_to_cart',
          ecommerce: {
            currency: 'TWD',
            value: totalValue,
            items: [{
              item_id: product.sku,
              item_name: product.name,
              item_category: product.category_name,
              price: finalPrice,
              quantity: quantity
            }]
          }
        });
      }

      // Facebook Pixel 加入購物車事件
      if (window.fbq) {
        window.fbq('track', 'AddToCart', {
          content_type: 'product',
          content_ids: [product.sku],
          content_name: product.name,
          value: totalValue,
          currency: 'TWD'
        });
      }

      // TikTok Pixel 加入購物車事件
      trackTikTokAddToCart(product, quantity);
    }
    return success;
  };

  const calculateDiscount = (original, sale) => {
    if (!original || original <= 0 || !sale) return 0;
    const discount = Math.round((1 - sale / original) * 100);
    return isNaN(discount) || !isFinite(discount) ? 0 : discount;
  };

  // 產生商品描述 (如果沒有描述，自動生成)
  const getProductDescription = () => {
    // 自動生成描述函數
    const generateAutoDesc = () => {
      return `${product.name}，優惠價 $${product.sale_price}！品質優選，超商取貨付款，LINE Pay 信用卡都能用。`;
    };

    if (product.description && product.description.trim()) {
      // 多次解碼以處理雙重編碼的情況
      let decoded = product.description;
      for (let i = 0; i < 3; i++) {
        decoded = decoded
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          .replace(/&#60;/g, '<')
          .replace(/&#62;/g, '>');
      }
      // 移除所有 HTML 標籤
      const cleaned = decoded.replace(/<[^>]*>/g, '').trim();
      // 檢查清理後的內容是否還有 HTML 殘留
      const hasHtmlResidue = /[<>&]|img|src|alt|href/.test(cleaned);
      // 如果有實際內容且沒有 HTML 殘留
      if (cleaned.length > 20 && !hasHtmlResidue) {
        return cleaned.substring(0, 300);
      }
    }
    // 使用自動生成的描述
    return generateAutoDesc();
  };

  if (loading) {
    return (
      <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {/* 麵包屑骨架 */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Skeleton width={40} height={24} />
            <Skeleton width={60} height={24} />
            <Skeleton width={80} height={24} />
          </Box>
          <Grid container spacing={4}>
            {/* 圖片區骨架 */}
            <Grid item xs={12} md={6}>
              <Skeleton variant="rectangular" height={450} sx={{ borderRadius: 1 }} />
              {/* 縮圖列骨架 */}
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} variant="rectangular" width={72} height={72} sx={{ borderRadius: 1 }} />
                ))}
              </Box>
            </Grid>
            {/* 商品資訊骨架 */}
            <Grid item xs={12} md={6}>
              <Skeleton width="30%" height={20} sx={{ mb: 1 }} />
              <Skeleton width="90%" height={36} sx={{ mb: 0.5 }} />
              <Skeleton width="70%" height={36} sx={{ mb: 2 }} />
              {/* 價格骨架 */}
              <Skeleton width="35%" height={48} sx={{ mb: 2 }} />
              <Divider sx={{ my: 2 }} />
              {/* 庫存骨架 */}
              <Skeleton width="25%" height={32} sx={{ mb: 2, borderRadius: 4 }} />
              {/* 數量選擇骨架 */}
              <Skeleton width="50%" height={40} sx={{ mb: 3 }} />
              {/* 按鈕骨架 */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Skeleton variant="rectangular" height={48} sx={{ flex: 1, borderRadius: 1 }} />
                <Skeleton variant="rectangular" height={48} sx={{ flex: 1, borderRadius: 1 }} />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }

  if (!product) {
    return (
      <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary">
              商品不存在或已下架
            </Typography>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/products')}
              sx={{ mt: 2 }}
            >
              返回商品列表
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  const discount = calculateDiscount(product.original_price, product.sale_price);
  const productDescription = getProductDescription();

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/*
          SEO meta（canonical / og:* / Product JSON-LD）由 server/middleware/seoMiddleware.js
          在 server-side 動態渲染。此處僅用於 SPA 內部導航時更新瀏覽器頁籤 title。
        */}
        <SEO
          title={product.name}
          description={productDescription}
          keywords={`${product.name},${product.category_name},批發,優惠,特價`}
        />

        {/* 麵包屑導航（加 href + preventDefault：點擊區域大、touch 友善、SEO 友善）
            position relative + zIndex 防止下方商品圖 Grid 溢出蓋住點擊事件 */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2, position: 'relative', zIndex: 1 }}
        >
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', py: 0.5 }}
            color="inherit"
            href="/"
            onClick={(e) => { e.preventDefault(); navigate('/'); }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            首頁
          </Link>
          <Link
            underline="hover"
            color="inherit"
            sx={{ cursor: 'pointer', py: 0.5 }}
            href="/products"
            onClick={(e) => { e.preventDefault(); navigate('/products'); }}
          >
            全部商品
          </Link>
          {product.category_name && (
            <Link
              underline="hover"
              color="inherit"
              sx={{ cursor: 'pointer', py: 0.5 }}
              href={`/products?category=${product.category_id}`}
              onClick={(e) => { e.preventDefault(); navigate(`/products?category=${product.category_id}`); }}
            >
              {product.category_name}
            </Link>
          )}
          <Typography color="text.primary">{product.name}</Typography>
        </Breadcrumbs>

        <Grid container spacing={4}>
        {/* 商品圖片 */}
        <Grid item xs={12} md={6}>
          {(() => {
            const productImages = parseImages(product);
            return (
              <>
                <Paper sx={{ p: 2, position: 'relative' }}>
                  <Box
                    component="img"
                    src={productImages[selectedImageIndex]}
                    alt={product.name}
                    title={product.name}
                    loading="lazy"
                    sx={{
                      width: '100%',
                      height: 450,
                      objectFit: 'contain',
                      cursor: productImages.length > 1 ? 'pointer' : 'default'
                    }}
                    onClick={() => {
                      if (productImages.length > 1) {
                        setSelectedImageIndex((prev) => (prev + 1) % productImages.length);
                      }
                    }}
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/600x400?text=圖片載入失敗'; }}
                  />
                </Paper>

                {/* 縮略圖選擇器 - 僅多圖時顯示 */}
                {productImages.length > 1 && (
                  <Box sx={{
                    display: 'flex',
                    gap: 1,
                    mt: 2,
                    overflowX: 'auto',
                    pb: 1,
                    '&::-webkit-scrollbar': { height: 6 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: '#ccc', borderRadius: 3 }
                  }}>
                    {productImages.map((img, idx) => (
                      <Box
                        key={idx}
                        onClick={() => setSelectedImageIndex(idx)}
                        sx={{
                          flexShrink: 0,
                          width: 72,
                          height: 72,
                          border: selectedImageIndex === idx ? '2px solid #1976d2' : '1px solid #ddd',
                          borderRadius: 1,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          opacity: selectedImageIndex === idx ? 1 : 0.7,
                          transition: 'all 0.2s',
                          '&:hover': { opacity: 1, borderColor: '#1976d2' }
                        }}
                      >
                        <Box
                          component="img"
                          src={img}
                          alt={`${product.name} ${idx + 1}`}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}

                {/* 圖片計數器 */}
                {productImages.length > 1 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                    {selectedImageIndex + 1} / {productImages.length} 張圖片（點擊主圖可切換）
                  </Typography>
                )}
              </>
            );
          })()}

          {/* 社群分享 */}
          <Box sx={{ mt: 2 }}>
            <SocialShare
              title={product.name}
              description={productDescription}
              url={`/products/${product.seo_url || product.id}`}
              image={product.image_url}
              product={product}
            />
          </Box>
        </Grid>

        {/* 商品資訊 */}
        <Grid item xs={12} md={6}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {product.category_name} | SKU: {product.sku}
          </Typography>

          <Typography variant="h5" component="h1" fontWeight="bold" gutterBottom>
            {product.name}
          </Typography>

          {/* 評分顯示 */}
          {reviewSummary && reviewSummary.total_reviews > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <StarRating
                value={reviewSummary.average_rating}
                readOnly
                size="small"
                totalReviews={reviewSummary.total_reviews}
              />
            </Box>
          )}

          {/* 優惠提示 - 僅在有庫存時顯示 */}
          {product.quantity > 0 && product.quantity <= 20 && (
            <Alert severity="info" icon={<LocalOfferIcon />} sx={{ mb: 2 }}>
              庫存即時更新
            </Alert>
          )}

          {/* 價格顯示 */}
          <Box sx={{ my: 2 }}>
            <Typography variant="h4" color="error" fontWeight="bold">
              ${product.sale_price}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* 庫存狀態 - 強化版 */}
          <Box sx={{ mb: 2 }}>
            {product.quantity > 10 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label="現貨供應" color="success" size="small" />
                {product.sold_count > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    已售 {product.sold_count} 件
                  </Typography>
                )}
              </Box>
            ) : product.quantity > 0 ? (
              <Paper
                sx={{
                  p: 1.5,
                  bgcolor: '#fff3e0',
                  border: '2px solid #ff9800',
                  borderRadius: 2
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label={`庫存 ${product.quantity} 件`} color="warning" size="small" />
                  {product.sold_count > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      已售 {product.sold_count} 件
                    </Typography>
                  )}
                </Box>
              </Paper>
            ) : (
              <Alert severity="error" sx={{ py: 0.5 }}>
                <Typography fontWeight="bold">已售完</Typography>
              </Alert>
            )}
          </Box>

          {/* 商品規格選項 */}
          {product.specs && parseSpecs(product.specs).length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: '#333' }}>
                規格選擇
              </Typography>
              {parseSpecs(product.specs).map((spec, idx) => (
                <Box key={idx} sx={{ mb: 1.5 }}>
                  <Typography variant="body2" fontWeight="500" sx={{ mb: 0.5, color: '#555' }}>
                    {spec.option_name}
                    {spec.required && <Chip label="必選" size="small" color="error" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {spec.values.map((option, optIdx) => (
                      <Box key={optIdx} sx={{ position: 'relative' }}>
                        {/* 如果選項有圖片，顯示圖片預覽 */}
                        {option.image && selectedOptions[spec.option_name] === option.name && (
                          <Box
                            component="img"
                            src={option.image}
                            alt={option.name}
                            sx={{
                              position: 'absolute',
                              top: -60,
                              left: 0,
                              width: 50,
                              height: 50,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '2px solid #1976d2',
                              zIndex: 10
                            }}
                          />
                        )}
                        <Chip
                          label={
                            option.price > 0
                              ? `${option.name} (+$${Math.round(option.price)})`
                              : option.name
                          }
                          variant={selectedOptions[spec.option_name] === option.name ? 'filled' : 'outlined'}
                          color={selectedOptions[spec.option_name] === option.name ? 'primary' : 'default'}
                          onClick={() => setSelectedOptions(prev => ({
                            ...prev,
                            [spec.option_name]: option.name
                          }))}
                          size="medium"
                          sx={{
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            height: 32,
                            '&:hover': { borderColor: '#1976d2' }
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
              {getOptionsPrice() > 0 && (
                <Typography variant="body2" color="error" fontWeight="bold" sx={{ mt: 1, pt: 1, borderTop: '1px dashed #ddd' }}>
                  規格加價：+${getOptionsPrice()}
                </Typography>
              )}
            </Paper>
          )}

          {/* 數量選擇 */}
          {product.quantity > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Typography>數量：</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #ddd', borderRadius: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                >
                  <RemoveIcon />
                </IconButton>
                <Typography sx={{ px: 2, minWidth: 40, textAlign: 'center' }}>
                  {quantity}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setQuantity(q => Math.min(product.quantity, q + 1))}
                  disabled={quantity >= product.quantity}
                >
                  <AddIcon />
                </IconButton>
              </Box>
              <Typography variant="body2" color="text.secondary">
                小計: ${(parseFloat(product.sale_price) + getOptionsPrice()) * quantity}
              </Typography>
            </Box>
          )}

          {/* 購買按鈕 */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<ShoppingCartIcon />}
              onClick={handleAddToCart}
              disabled={product.quantity <= 0}
              sx={{ flex: 1 }}
            >
              加入購物車
            </Button>
            <Button
              variant="outlined"
              size="large"
              color="error"
              onClick={async () => {
                const success = await handleAddToCart();
                if (success) navigate('/checkout');
              }}
              disabled={product.quantity <= 0}
              sx={{ flex: 1 }}
            >
              立即購買
            </Button>
          </Box>

          {/* 購物須知 - 簡潔橫列式 */}
          <Paper variant="outlined" sx={{ mt: 2, p: 1.5, bgcolor: '#f8f9fa' }}>
            <Grid container spacing={1.5}>
              {product.weight && (
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <InventoryIcon sx={{ fontSize: 18, color: '#666' }} />
                    <Typography variant="body2" color="text.secondary">
                      重量: {product.weight}g
                    </Typography>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} sm={product.weight ? 4 : 6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <LocalShippingIcon sx={{ fontSize: 18, color: '#666' }} />
                  <Typography variant="body2" color="text.secondary">
                    超商取貨 $60 | 3-5天送達
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={product.weight ? 4 : 6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <AssignmentReturnIcon sx={{ fontSize: 18, color: '#666' }} />
                  <Typography variant="body2" color="text.secondary">
                    七日猶豫期 | 瑕疵可換
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

        {/* 商品介紹區塊 - 獨立全寬 */}
        <Paper sx={{ p: 3, mt: 4 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            商品介紹
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {/* 商品詳情圖片 - 置中顯示 */}
          {product.description && product.description.includes('img') && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0,
              mb: 2
            }}>
              {(() => {
                // 解碼 HTML entities 並提取圖片
                const decoded = product.description
                  .replace(/&lt;/g, '<')
                  .replace(/&gt;/g, '>')
                  .replace(/&amp;/g, '&')
                  .replace(/&quot;/g, '"')
                  .replace(/&#39;/g, "'");
                const imgRegex = /src\s*=\s*['"]([^'"]+)['"]/g;
                const images = [];
                let match;
                while ((match = imgRegex.exec(decoded)) !== null) {
                  let imgUrl = match[1];
                  // 轉換相對路徑為 YMShop 絕對路徑
                  if (imgUrl.startsWith('/image/')) {
                    imgUrl = `https://www.ymshop.com.tw${imgUrl}`;
                  }
                  images.push(imgUrl);
                }
                return images.map((img, idx) => (
                  <Box
                    key={idx}
                    component="img"
                    src={img}
                    alt={`${product.name} 商品詳情 ${idx + 1}`}
                    loading="lazy"
                    sx={{
                      maxWidth: '100%',
                      width: 'auto',
                      maxHeight: 800,
                      height: 'auto',
                      display: 'block',
                      margin: '0 auto',
                      padding: 0
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ));
              })()}
            </Box>
          )}

          {/* 商品描述文字 */}
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ whiteSpace: 'pre-line', textAlign: 'center', mt: 2 }}
          >
            {productDescription}
          </Typography>
        </Paper>

        {/* 商品評論區塊 */}
        <Paper sx={{ p: 3, mt: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              商品評價
            </Typography>
            <Button
              variant={showReviewForm ? 'outlined' : 'contained'}
              startIcon={<RateReviewIcon />}
              onClick={() => setShowReviewForm(!showReviewForm)}
            >
              {showReviewForm ? '取消評論' : '撰寫評論'}
            </Button>
          </Box>
          <Divider sx={{ mb: 3 }} />

          {/* 評論表單 */}
          {showReviewForm && (
            <Box sx={{ mb: 4 }}>
              <ReviewForm
                productId={product.id}
                onSuccess={(data) => {
                  setShowReviewForm(false);
                  // 重新載入評論摘要
                  api.get(`/reviews/product/${product.id}`).then(res => {
                    if (res.data.success) {
                      setReviewSummary(res.data.data.summary);
                    }
                  });
                }}
                onCancel={() => setShowReviewForm(false)}
              />
            </Box>
          )}

          {/* 評論列表 */}
          {!showReviewForm && <ProductReviews productId={product.id} />}
        </Paper>

        {/* 相關商品推薦 */}
        <RelatedProducts productId={product.id} limit={6} />
      </Container>
    </Box>
  );
};

export default ProductDetail;
